import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { ImageCropData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import EditableH1TitleComponent from 'webapp/components/editable-h1-title';
import FileLogoWithUploadComponent from 'webapp/components/file/logo-with-upload';
import ReportsMasterDateRangeSwitchComponent from 'webapp/components/reports/master-date-range-switch';

import type ReportSlide from 'webapp/models-custom/report-slide';

import type { TrackedEvent } from 'webapp/services/analytics';

export interface Args {
	model: ReportSlide;
	hideBranding: boolean;
	canEdit?: boolean;
}

export default class ReportsSlideBottomInfoComponent extends Component<Args> {
	@service('request') declare requestService: Service.Request;
	@service('navigation') declare navigationService: Service.Navigation;

	get trackingObj(): TrackedEvent | undefined {
		if (this.args.canEdit) {
			return { event: TrackingEvent.REPORT_EDITED, data: { origin: 'editor' } };
		}
		return undefined;
	}

	get isSampleDataWarningShown(): boolean {
		const reportSlide = this.args.model,
			isTemplate = !!reportSlide.report?.isTemplate,
			isAnyFragmentEligibleForWarningToBeShown = reportSlide.fragments.some(
				(fragment) => !!fragment.blockId,
			);

		return isTemplate && isAnyFragmentEligibleForWarningToBeShown;
	}

	get isMasterDateRangeSwitchShown(): boolean {
		return (
			!this.navigationService.isLoadedFromSnapshooter &&
			!!this.args.canEdit &&
			this.args.model.hasFragmentsWithMetricData
		);
	}

	@action
	changeLogo(url: string, originalUrl: string, crop: ImageCropData | null): void {
		const slide = this.args.model;
		slide.logo = url;
		slide.logoOriginal = originalUrl;
		slide.logoCrop = crop;
		slide.save().catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}
	<template>
		<div class="bottomInfo {{if @canEdit 'canEdit'}}">
			<div class="bottomInfo-left">
				<FileLogoWithUploadComponent
					@image={{@model.shownLogo}}
					@originalImage={{@model.logoOriginal}}
					@imageCrop={{@model.logoCrop}}
					@canEdit={{@canEdit}}
					@onChange={{this.changeLogo}}
				/>

				<EditableH1TitleComponent
					@model={{@model}}
					@canEdit={{@canEdit}}
					@trackingObj={{this.trackingObj}}
				/>
			</div>
			<div class="bottomInfo-right">
				{{#if this.isMasterDateRangeSwitchShown}}
					<ReportsMasterDateRangeSwitchComponent
						@model={{@model}}
						@theme={{@model.bgTheme}}
					/>
				{{/if}}

				{{#if this.isSampleDataWarningShown}}
					<div class="warning">
						<div class="sample">Note: Showing sample data</div>
					</div>
				{{/if}}

				{{#unless @hideBranding}}
					<a href="https://databox.com" target="_blank"><span class="powered">Powered by</span></a>
				{{/unless}}
			</div>
		</div>
	</template>
}
